import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { apiClient } from '@/lib/api/client';
import type { AcademicYear } from '@/lib/api/types';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useApiQuery } from '@/lib/hooks/useApiQuery';

interface AcademicYearContextValue {
  years: AcademicYear[];
  loading: boolean;
  selected: AcademicYear | null;
  select(id: string): void;
  reload(): void;
}

const Ctx = createContext<AcademicYearContextValue>({
  years: [],
  loading: false,
  selected: null,
  select: () => undefined,
  reload: () => undefined,
});

const storageKey = (organizationId: string) => `intellschools.academicYear.${organizationId}`;

function readStored(organizationId: string | undefined): string | null {
  if (!organizationId) return null;
  try {
    return localStorage.getItem(storageKey(organizationId));
  } catch {
    return null;
  }
}

/**
 * Academic-year selector state. The selection is a per-user UI preference (stored locally per
 * organization) that filters views; it is not an authorization boundary.
 */
export function AcademicYearProvider({ children }: { children: ReactNode }) {
  const { me, can } = useAuth();
  const orgId = me?.context.organization?.id;
  const schoolId = me?.context.school?.id;
  const enabled = Boolean(orgId) && can('academic_year.read');

  const query = useApiQuery(enabled ? `years:${orgId}:${schoolId ?? ''}` : null, (signal) =>
    apiClient
      .get<{ data: AcademicYear[] }>('/academic-years', { query: { schoolId }, signal })
      .then((r) => r.data.filter((y) => y.status !== 'closed' || y.isCurrent)),
  );

  const [choice, setChoice] = useState<{ org: string | undefined; id: string | null }>({
    org: orgId,
    id: readStored(orgId),
  });
  const storedId = choice.org === orgId ? choice.id : readStored(orgId);

  const years = useMemo(() => query.data ?? [], [query.data]);
  const selected = useMemo(() => {
    if (years.length === 0) return null;
    return (
      years.find((y) => y.id === storedId) ??
      years.find((y) => y.isCurrent && y.schoolId === (schoolId ?? null)) ??
      years.find((y) => y.isCurrent) ??
      years[0] ??
      null
    );
  }, [years, storedId, schoolId]);

  const select = useCallback(
    (id: string) => {
      setChoice({ org: orgId, id });
      try {
        if (orgId) localStorage.setItem(storageKey(orgId), id);
      } catch {
        // Storage unavailable (private mode): selection lasts for this page view only.
      }
    },
    [orgId],
  );

  const value = useMemo(
    () => ({ years, loading: query.loading, selected, select, reload: query.reload }),
    [years, query.loading, selected, select, query.reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAcademicYear(): AcademicYearContextValue {
  return useContext(Ctx);
}
